import React, { useEffect, useState } from 'react';
import SEO from './SEO';
import config from '../utils/config';

const API_URL = config.url;

// Utility to extract YouTube video ID
function getYouTubeId(url) {
  const match = url.match(/(?:youtu.be\/|youtube.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  return match ? match[1] : null;
}

const VideoCardSkeleton = () => (
  <div className="animate-pulse bg-white rounded-xl shadow p-4 flex flex-col items-center">
    <div className="w-full aspect-video bg-gray-200 rounded mb-3" />
    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
    <div className="h-3 bg-gray-100 rounded w-1/2 mb-1" />
    <div className="h-3 bg-gray-100 rounded w-1/3" />
  </div>
);

const VideoModal = ({ open, onClose, video }) => {
  if (!open || !video) return null;
  const isYouTube = getYouTubeId(video.video_url);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
      <div className="relative bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 p-4 flex flex-col items-center">
        <button
          className="absolute top-2 right-2 text-gray-600 hover:text-orange-600 text-2xl font-bold"
          onClick={onClose}
          aria-label="बंद करें"
        >
          ×
        </button>
        <div className="w-full aspect-video mb-4 bg-black rounded-lg overflow-hidden flex items-center justify-center">
          {isYouTube ? (
            <iframe
              width="100%"
              height="100%"
              src={`https://www.youtube.com/embed/${getYouTubeId(video.video_url)}?autoplay=1`}
              title={video.title}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="w-full h-full"
            />
          ) : video.video_url && video.video_url.startsWith('/uploads/') ? (
            <video controls autoPlay className="w-full h-full rounded-lg bg-black">
              <source src={video.video_url} type="video/mp4" />
              आपका ब्राउज़र वीडियो टैग का समर्थन नहीं करता।
            </video>
          ) : null}
        </div>
        <h2 className="text-xl font-bold mb-1 text-center text-gray-800">{video.title}</h2>
        {video.description && (
          <p className="text-gray-600 text-sm text-center mb-2">{video.description}</p>
        )}
        {video.category && (
          <span className="text-xs bg-orange-100 text-orange-700 rounded px-2 py-1 mt-1 mb-2 font-semibold">
            {video.category}
          </span>
        )}
      </div>
    </div>
  );
};

const VideoPage = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await fetch(`${API_URL}/videos`);
        const contentType = res.headers.get('content-type');
        if (!res.ok) throw new Error('वीडियो लाने में विफल');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('सर्वर से अप्रत्याशित प्रतिक्रिया।');
        }
        const data = await res.json();
        setVideos(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchVideos();
  }, []);

  const handleCardClick = (video) => {
    setSelectedVideo(video);
    setModalOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 hindi-text">
              वीडियो गैलरी
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto hindi-text">
              हमारे वीडियो देखें और जानें कि हम कैसे समाज के विकास के लिए कार्य कर रहे हैं।
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <VideoCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 hindi-text">
            वीडियो गैलरी
          </h1>
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
            <p className="text-red-800 hindi-text">
              वीडियो लोड करने में त्रुटि: {error}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-yellow-50 py-16">
      <SEO title="वीडियो गैलरी" description="हमारे वीडियो देखें और जानें कि हम कैसे समाज के विकास के लिए कार्य कर रहे हैं।" />
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 hindi-text">
            वीडियो गैलरी
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto hindi-text">
            हमारे वीडियो देखें और जानें कि हम कैसे समाज के विकास के लिए कार्य कर रहे हैं।
          </p>
        </div>

        {videos.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md mx-auto">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 hindi-text">
                कोई वीडियो नहीं मिला
              </h3>
              <p className="text-gray-600 hindi-text">
                अभी कोई वीडियो उपलब्ध नहीं है। कृपया बाद में पुनः जांचें।
              </p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video) => (
              <div
                key={video.id}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow cursor-pointer overflow-hidden"
                onClick={() => handleCardClick(video)}
              >
                <div className="relative">
                  {getYouTubeId(video.video_url) ? (
                    <img
                      src={`https://img.youtube.com/vi/${getYouTubeId(video.video_url)}/maxresdefault.jpg`}
                      alt={video.title}
                      className="w-full aspect-video object-cover"
                      onError={(e) => {
                        e.target.src = `https://img.youtube.com/vi/${getYouTubeId(video.video_url)}/hqdefault.jpg`;
                      }}
                    />
                  ) : video.video_url && video.video_url.startsWith('/uploads/') ? (
                    <video className="w-full aspect-video object-cover">
                      <source src={video.video_url} type="video/mp4" />
                    </video>
                  ) : (
                    <div className="w-full aspect-video bg-gray-200 flex items-center justify-center">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black bg-opacity-20 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                    <div className="w-16 h-16 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                      <svg className="w-8 h-8 text-orange-600" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M8 5v14l11-7z" />
                      </svg>
                    </div>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 hindi-text line-clamp-2">
                    {video.title}
                  </h3>
                  {video.description && (
                    <p className="text-gray-600 text-sm hindi-text line-clamp-3">
                      {video.description}
                    </p>
                  )}
                  {video.category && (
                    <span className="inline-block bg-orange-100 text-orange-700 text-xs rounded px-2 py-1 mt-2 font-semibold">
                      {video.category}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <VideoModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        video={selectedVideo}
      />
    </div>
  );
};

export default VideoPage;